import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, parseISO, isToday } from "date-fns";
import { Megaphone, Users, MessageSquare, Plus, Send, Search, LogOut, UserPlus, Eye, Paperclip, Repeat, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const when = (iso) => { try { const d = parseISO(iso); return isToday(d) ? format(d, "HH:mm") : format(d, "d MMM HH:mm"); } catch { return ""; } };
const Icon = ({ type }) => type === "announcements" ? <Megaphone className="w-4 h-4" /> : type === "everyone" ? <Users className="w-4 h-4" /> : type === "group" ? <Users className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />;

// oversight=true → admin / welfare-officer review of ALL conversations (read-only unless a member).
export default function Chat({ oversight = false }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState(null);
  const [activeId, setActiveId] = useState(params.get("c") || null);
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(oversight ? "/admin/chat/conversations" : "/chat/conversations"); setConvs(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setConvs([]); }
  };
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [oversight]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeId && !oversight) setParams(p => { const n = new URLSearchParams(p); n.set("c", activeId); return n; }, { replace: true }); }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = useMemo(() => (convs || []).find(c => c.id === activeId) || null, [convs, activeId]);
  const visible = useMemo(() => (convs || []).filter(c => !q || c.title.toLowerCase().includes(q.toLowerCase()) || (c.last_message_preview || "").toLowerCase().includes(q.toLowerCase())), [convs, q]);

  return (
    <div className={`${oversight ? "" : "max-w-6xl mx-auto px-4 py-6"}`} data-testid={oversight ? "chat-oversight" : "chat-page"}>
      {!oversight && (
        <div className="mb-4 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Club chat</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">Chat</h1>
          </div>
          <button onClick={() => setNewOpen(true)} data-testid="chat-new-btn" className="btn-volt px-4 py-2.5 rounded inline-flex items-center gap-2 text-[10px] tracking-widest uppercase"><Plus className="w-3.5 h-3.5" /> New chat</button>
        </div>
      )}
      <div className="grid md:grid-cols-[300px_1fr] gap-4 min-h-[60vh]">
        <aside className={`card-tactical rounded-md overflow-hidden flex flex-col ${active ? "hidden md:flex" : "flex"}`} data-testid="chat-list">
          <div className="p-3 border-b border-border relative">
            <Search className="w-3.5 h-3.5 absolute left-6 top-1/2 -translate-y-1/2 opacity-50" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search chats" className="pl-8 h-9" data-testid="chat-search" />
          </div>
          <div className="overflow-y-auto flex-1">
            {convs === null ? <div className="p-4 opacity-50 text-xs tracking-widest">LOADING…</div>
              : visible.length === 0 ? <div className="p-6 text-center text-sm opacity-60">No chats yet — start one with “New chat”.</div>
              : visible.map(c => (
                <button key={c.id} onClick={() => setActiveId(c.id)} data-testid={`chat-conv-${c.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-muted/40 transition-colors ${c.id === activeId ? "bg-[#83B68B]/10 border-l-2 border-l-[#83B68B]" : ""}`}>
                  <div className="flex items-center gap-2">
                    <span className={`${c.type === "announcements" ? "text-amber-400" : "text-[#83B68B]"}`}><Icon type={c.type} /></span>
                    <span className="font-semibold text-sm truncate flex-1">{c.title}</span>
                    {c.unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid={`chat-unread-${c.id}`}>{c.unread}</span>}
                    {c.last_message_at && <span className="text-[10px] opacity-50">{when(c.last_message_at)}</span>}
                  </div>
                  <div className="text-xs opacity-60 truncate mt-0.5">{c.last_message_preview || (c.type === "group" ? `${c.members.length} members` : oversight && c.type === "dm" ? `${c.message_count} messages` : "No messages yet")}</div>
                </button>
              ))}
          </div>
        </aside>
        <section className={`card-tactical rounded-md flex flex-col min-h-[60vh] ${active ? "flex" : "hidden md:flex"}`}>
          {active ? <Thread conv={active} user={user} oversight={oversight} onBack={() => setActiveId(null)} onChanged={load} onAddPeople={() => setAddOpen(true)} />
            : <div className="flex-1 flex items-center justify-center text-sm opacity-50 p-8 text-center">Pick a conversation, or start a new one.</div>}
        </section>
      </div>
      <NewChatDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={(c) => { setNewOpen(false); load(); setActiveId(c.id); }} />
      {active && <AddPeopleDialog open={addOpen} conv={active} onClose={() => setAddOpen(false)} onChanged={load} />}
    </div>
  );
}

function Thread({ conv, user, oversight, onBack, onChanged, onAddPeople }) {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [attach, setAttach] = useState(null);
  const [swapOpen, setSwapOpen] = useState(false);
  const [mentionQ, setMentionQ] = useState(null);
  const [people, setPeople] = useState([]);
  useEffect(() => { if (conv.type === "everyone" && people.length === 0) api.get("/chat/users").then(r => setPeople(r.data)).catch(() => {}); }, [conv.type, people.length]);
  const onType = (v) => {
    setText(v);
    const m = conv.type === "everyone" ? v.match(/(?:^|\s)@([\w' -]{0,30})$/) : null;
    setMentionQ(m ? m[1].toLowerCase() : null);
  };
  const insertMention = (name) => { setText(t => t.replace(/@([\w' -]{0,30})$/, `@${name} `)); setMentionQ(null); };
  const mentionHits = mentionQ !== null ? people.filter(p => (p.name || "").toLowerCase().includes(mentionQ)).slice(0, 6) : [];
  const renderBody = (b) => b.split(/(@[A-Z][\w']*(?: [A-Z][\w']*)?)/g).map((part, i) => part.startsWith("@") ? <span key={i} className="font-bold underline decoration-dotted">{part}</span> : part);
  const fileRef = useRef(null);
  const bottom = useRef(null);
  const lastAt = useRef(null);
  const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
  const pickFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try { const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setAttach(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); } finally { setBusy(false); }
  };

  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    setMsgs(null); lastAt.current = null;
    let stop = false;
    const pull = async () => {
      try {
        const { data } = await api.get(`/chat/conversations/${conv.id}/messages`, { params: lastAt.current ? { after: lastAt.current } : {} });
        if (stop) return;
        if (data.length) {
          const stamps = data.flatMap(m => [m.created_at, m.deleted_at].filter(Boolean));
          lastAt.current = stamps.reduce((a, b) => (a > b ? a : b), lastAt.current || "");
          setMsgs(prev => {
            const list = [...(prev || [])];
            data.forEach(m => { const i = list.findIndex(x => x.id === m.id); if (i >= 0) list[i] = m; else list.push(m); });
            return list;
          });
        }
        else setMsgs(prev => prev || []);
      } catch { if (!stop) setMsgs(prev => prev || []); }
    };
    pull();
    const id = setInterval(pull, 4000);
    return () => { stop = true; clearInterval(id); };
  }, [conv.id, refreshKey]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs?.length]);

  const decideSwap = async (swapId, action) => {
    let reason = "";
    if (action === "decline") {
      reason = window.prompt("Reason for declining (optional)") ?? null;
      if (reason === null) return;
    }
    setBusy(true);
    const optimistic = action === "accept" ? "accepted" : "declined";
    setMsgs(ms => (ms || []).map(m => m.swap?.id === swapId ? { ...m, swap: { ...m.swap, status: optimistic, can_decide: false } } : m));
    try {
      await api.post(`/booking-swaps/${swapId}/${action}`, action === "decline" ? { reason } : {});
      toast.success(action === "accept" ? "Swap accepted — bookings updated" : "Swap declined");
      setRefreshKey(k => k + 1);
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setRefreshKey(k => k + 1); } finally { setBusy(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body && !attach) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/chat/conversations/${conv.id}/messages`, { body, attachment_path: attach?.path || null, attachment_name: attach?.name || null, attachment_type: attach?.content_type || null });
      setMsgs(m => [...(m || []), data]); lastAt.current = data.created_at; setText(""); setAttach(null); onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const leave = async () => {
    if (!window.confirm(`Leave “${conv.title}”?`)) return;
    try { await api.post(`/chat/conversations/${conv.id}/leave`); toast.success("Left chat"); onBack(); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteMsg = async (m) => {
    if (!window.confirm("Delete this message for everyone?")) return;
    try { const { data } = await api.delete(`/chat/messages/${m.id}`); setMsgs(ms => (ms || []).map(x => x.id === m.id ? data : x)); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const readOnly = !conv.is_member || !conv.can_post;
  return (
    <>
      <header className="px-4 py-3 border-b border-border flex items-center gap-3" data-testid="chat-thread-header">
        <button onClick={onBack} className="md:hidden text-xs opacity-70" data-testid="chat-back">← Back</button>
        <span className={conv.type === "announcements" ? "text-amber-400" : "text-[#83B68B]"}><Icon type={conv.type} /></span>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold tracking-tight truncate">{conv.title}</div>
          <div className="text-[10px] opacity-60 truncate">
            {conv.type === "everyone" ? "Whole club · anyone can post" : conv.type === "announcements" ? "Whole club · admins post" : conv.members.map(m => m.name).join(", ")}
          </div>
        </div>
        {oversight && !conv.is_member && <span className="text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border border-amber-500/50 text-amber-400 inline-flex items-center gap-1" data-testid="chat-oversight-badge"><Eye className="w-3 h-3" /> Reviewing</span>}
        {conv.type === "dm" && conv.is_member && (
          <button onClick={() => setSwapOpen(true)} title="Propose a booking swap" data-testid="chat-propose-swap" className="text-[10px] tracking-widest uppercase px-2.5 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1"><Repeat className="w-3 h-3" /> Propose swap</button>
        )}
        {conv.type === "group" && conv.is_member && (conv.created_by === user?.id || user?.role === "admin") && (
          <button onClick={onAddPeople} title="Add people" data-testid="chat-add-people" className="p-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B]"><UserPlus className="w-3.5 h-3.5" /></button>
        )}
        {["group", "dm"].includes(conv.type) && conv.is_member && (
          <button onClick={leave} title="Leave chat" data-testid="chat-leave" className="p-1.5 rounded border border-border hover:border-red-500 hover:text-red-400"><LogOut className="w-3.5 h-3.5" /></button>
        )}
      </header>
      <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
        {msgs === null ? <div className="opacity-50 text-xs tracking-widest">LOADING…</div>
          : msgs.length === 0 ? <div className="text-center text-sm opacity-50 py-10">No messages yet{readOnly ? "." : " — say hello."}</div>
          : msgs.map(m => {
            const mine = m.sender_id === user?.id;
            if (m.deleted_at) {
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                  <div className="max-w-[78%] rounded-2xl px-3.5 py-2 text-xs italic border border-dashed border-border opacity-50" data-testid={`chat-msg-deleted-${m.id}`}>
                    {m.deleted_by_admin ? "Message removed by an admin" : "Message deleted"}
                    <span className="not-italic ml-2 text-[9px]">{when(m.created_at)}</span>
                  </div>
                </div>
              );
            }
            const canDelete = !(m.is_system || m.sender_id === "system") && (mine || user?.role === "admin");
            if (m.is_system || m.sender_id === "system") {
              return (
                <div key={m.id} className="flex justify-center" data-testid={`chat-msg-${m.id}`}>
                  <div className="max-w-[85%] rounded-xl border border-[#83B68B]/40 bg-[#83B68B]/10 px-3.5 py-2 text-xs" data-testid="chat-system-msg">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#83B68B] mb-0.5">{m.sender_name}</div>
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    {m.swap?.can_decide && (
                      <div className="flex gap-2 mt-2" data-testid={`swap-quick-${m.swap.id}`}>
                        <button type="button" disabled={busy} onClick={() => decideSwap(m.swap.id, "accept")} data-testid="swap-quick-accept"
                          className="flex-1 rounded bg-[#83B68B] text-white py-1.5 text-[10px] font-bold tracking-widest uppercase hover:opacity-90 disabled:opacity-50">Accept</button>
                        <button type="button" disabled={busy} onClick={() => decideSwap(m.swap.id, "decline")} data-testid="swap-quick-decline"
                          className="flex-1 rounded border border-red-500/60 text-red-400 py-1.5 text-[10px] font-bold tracking-widest uppercase hover:bg-red-500/10 disabled:opacity-50">Decline</button>
                      </div>
                    )}
                    {m.swap && m.swap.status !== "pending" && (
                      <div className="mt-1.5 text-[10px] uppercase tracking-widest opacity-60" data-testid="swap-quick-status">Swap {m.swap.status}</div>
                    )}
                    <div className="text-[9px] mt-1 opacity-50 text-right">{when(m.created_at)}</div>
                  </div>
                </div>
              );
            }
            return (
              <div key={m.id} className={`group flex items-end gap-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                {canDelete && mine && <button type="button" onClick={() => deleteMsg(m)} title="Delete message" data-testid={`chat-msg-delete-${m.id}`} className="opacity-0 group-hover:opacity-70 focus:opacity-100 hover:!opacity-100 p-1 rounded text-red-400 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>}
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-[#83B68B] text-white rounded-br-sm" : "bg-muted rounded-bl-sm"}`}>
                  {!mine && conv.type !== "dm" && <div className="text-[10px] font-bold opacity-70 mb-0.5">{m.sender_name}</div>}
                  {m.attachment?.path && (
                    (m.attachment.type || "").startsWith("image/")
                      ? <a href={fileUrl(m.attachment.path)} target="_blank" rel="noreferrer" data-testid={`chat-attach-${m.id}`}><img src={fileUrl(m.attachment.path)} alt={m.attachment.name || "attachment"} className="rounded-lg max-h-64 max-w-full mb-1 border border-white/10" /></a>
                      : <a href={fileUrl(m.attachment.path)} target="_blank" rel="noreferrer" data-testid={`chat-attach-${m.id}`} className={`inline-flex items-center gap-1.5 text-xs underline mb-1 ${mine ? "text-white" : "text-[#83B68B]"}`}><Paperclip className="w-3 h-3" /> {m.attachment.name || "Attachment"}</a>
                  )}
                  {m.body && <div className="whitespace-pre-wrap break-words">{renderBody(m.body)}</div>}
                  <div className={`text-[9px] mt-1 ${mine ? "text-white/70" : "opacity-50"} text-right`}>{when(m.created_at)}</div>
                </div>
                {canDelete && !mine && <button type="button" onClick={() => deleteMsg(m)} title="Delete message (admin)" data-testid={`chat-msg-delete-${m.id}`} className="opacity-0 group-hover:opacity-70 focus:opacity-100 hover:!opacity-100 p-1 rounded text-red-400 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            );
          })}
        <div ref={bottom} />
      </div>
      {readOnly ? (
        <div className="px-4 py-3 border-t border-border text-xs opacity-60" data-testid="chat-readonly">
          {conv.type === "announcements" ? "Only admins can post announcements." : "You're reviewing this conversation — posting is disabled."}
        </div>
      ) : (
        <form onSubmit={send} className="p-3 border-t border-border space-y-2" data-testid="chat-compose">
          {attach && (
            <div className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded border border-[#83B68B]/40 bg-[#83B68B]/10" data-testid="chat-attach-pending">
              <Paperclip className="w-3 h-3" /> {attach.name} <button type="button" onClick={() => setAttach(null)} className="opacity-70 hover:text-red-400" aria-label="Remove attachment">×</button>
            </div>
          )}
          {mentionHits.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="chat-mention-suggestions">
              {mentionHits.map(p => <button key={p.id} type="button" onClick={() => insertMention(p.name)} data-testid={`chat-mention-${p.id}`} className="text-xs px-2 py-1 rounded-full border border-[#83B68B]/40 bg-[#83B68B]/10 hover:bg-[#83B68B]/20">@{p.name}</button>)}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <input ref={fileRef} type="file" accept="image/*,.pdf,.docx,.csv,.txt" className="hidden" onChange={pickFile} data-testid="chat-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} title="Attach a photo or file" data-testid="chat-attach-btn" className="h-10 w-10 rounded border border-border inline-flex items-center justify-center hover:border-[#83B68B] hover:text-[#83B68B] disabled:opacity-40"><Paperclip className="w-4 h-4" /></button>
            <Textarea rows={1} value={text} onChange={e => onType(e.target.value)} placeholder={conv.type === "everyone" ? "Write a message… (@name to mention)" : "Write a message…"} data-testid="chat-input" className="min-h-[40px] max-h-32 resize-none"
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <button type="submit" disabled={busy || (!text.trim() && !attach)} data-testid="chat-send" className="btn-volt h-10 w-10 rounded inline-flex items-center justify-center disabled:opacity-40"><Send className="w-4 h-4" /></button>
          </div>
        </form>
      )}
      {swapOpen && <SwapFromChatDialog conv={conv} onClose={() => setSwapOpen(false)} />}
    </>
  );
}

function SwapFromChatDialog({ conv, onClose }) {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/chat/conversations/${conv.id}/swap-candidates`).then(r => setRows(r.data)).catch(err => { toast.error(formatApiError(err.response?.data?.detail)); setRows([]); }); }, [conv.id]);
  const hhmm = (h, m) => `${String(h).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="chat-swap-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Propose a swap with {conv.title}</DialogTitle></DialogHeader>
        <p className="text-xs opacity-60">Pick which of their upcoming bookings you'd like — you'll then choose which of yours to offer in return.</p>
        <div className="max-h-72 overflow-y-auto space-y-1.5">
          {rows === null ? <div className="opacity-50 text-xs tracking-widest">LOADING…</div>
            : rows.length === 0 ? <div className="text-sm opacity-60 p-4 text-center" data-testid="chat-swap-empty">{conv.title} has no upcoming bookings to swap.</div>
            : rows.map(b => (
              <button key={b.id} type="button" onClick={() => { onClose(); nav(`/app/bookings?swap_target=${b.id}`); }} data-testid={`chat-swap-target-${b.id}`}
                className="w-full text-left card-tactical rounded p-3 hover:border-[#83B68B]/60 transition-colors">
                <div className="font-semibold text-sm">{b.booking_date} · {hhmm(b.start_hour, b.start_min)}–{hhmm(b.end_hour, b.end_min)}</div>
                <div className="text-xs opacity-70">{b.venue_short_code ? `[${b.venue_short_code}] ` : ""}{b.pitch_name} · {b.age_group ? `${b.age_group} ` : ""}{b.team_name || ""}{b.opponent ? ` vs ${b.opponent}` : ""} · {b.session_type}</div>
              </button>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UserMultiPick({ selected, setSelected, exclude = [] }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/chat/users").then(r => setUsers(r.data)).catch(() => {}); }, []);
  const list = users.filter(u => !exclude.includes(u.id) && (!q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase())));
  return (
    <div>
      <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Type a name to find people…" data-testid="chat-people-search" />
      {selected.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{selected.map(id => { const u = users.find(x => x.id === id); return <button key={id} type="button" onClick={() => setSelected(selected.filter(x => x !== id))} className="text-xs px-2 py-1 rounded-full bg-[#83B68B]/15 border border-[#83B68B]/40 text-[#83B68B]" data-testid={`chat-pick-chip-${id}`}>{u?.name || "…"} ×</button>; })}</div>}
      <div className="mt-2 max-h-48 overflow-y-auto border border-border rounded">
        {list.length === 0 && <div className="p-3 text-xs opacity-50">No one matches.</div>}
        {list.map(u => (
          <button key={u.id} type="button" onClick={() => setSelected(selected.includes(u.id) ? selected.filter(x => x !== u.id) : [...selected, u.id])} data-testid={`chat-pick-${u.id}`}
            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-muted/50 ${selected.includes(u.id) ? "bg-[#83B68B]/10" : ""}`}>
            <span className={`w-4 h-4 rounded border inline-flex items-center justify-center text-[10px] ${selected.includes(u.id) ? "bg-[#83B68B] border-[#83B68B] text-white" : "border-border"}`}>{selected.includes(u.id) ? "✓" : ""}</span>
            <span className="font-medium">{u.name}</span><span className="text-xs opacity-50">· {u.role}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function NewChatDialog({ open, onClose, onCreated }) {
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSelected([]); setName(""); } }, [open]);
  const isGroup = selected.length > 1;
  const create = async () => {
    if (selected.length === 0) return toast.error("Pick at least one person");
    if (isGroup && !name.trim()) return toast.error("Give the group a name");
    setBusy(true);
    try { const { data } = await api.post("/chat/conversations", { type: isGroup ? "group" : "dm", member_ids: selected, name: isGroup ? name.trim() : null }); onCreated(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="chat-new-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">New chat</DialogTitle></DialogHeader>
        <p className="text-xs opacity-60">Pick one person for a direct chat, or several to create a group.</p>
        <UserMultiPick selected={selected} setSelected={setSelected} />
        {isGroup && <Input value={name} onChange={e => setName(e.target.value)} placeholder="Group name, e.g. U12 coaches" maxLength={80} data-testid="chat-group-name" />}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border">Cancel</button>
          <button type="button" onClick={create} disabled={busy} data-testid="chat-create" className="btn-volt text-[10px] tracking-widest uppercase px-4 py-2 rounded disabled:opacity-40">{isGroup ? "Create group" : "Start chat"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddPeopleDialog({ open, conv, onClose, onChanged }) {
  const [selected, setSelected] = useState([]);
  useEffect(() => { if (open) setSelected([]); }, [open]);
  const add = async () => {
    try { await api.post(`/chat/conversations/${conv.id}/members`, { add_ids: selected }); toast.success("Added"); onChanged(); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="chat-add-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Add people to “{conv.title}”</DialogTitle></DialogHeader>
        <UserMultiPick selected={selected} setSelected={setSelected} exclude={conv.members.map(m => m.id)} />
        <div className="flex justify-end"><button type="button" onClick={add} disabled={!selected.length} data-testid="chat-add-confirm" className="btn-volt text-[10px] tracking-widest uppercase px-4 py-2 rounded disabled:opacity-40">Add</button></div>
      </DialogContent>
    </Dialog>
  );
}
